import { useQuery } from "@tanstack/react-query";

import useApiHelper from "../../hooks/useApiHelper";
import { IDefaultResponse } from "../../models/DefaultResponse";
import {
  IAiResultFilterPrompt,
  IInitialPriceEstimateCategorySummary,
  IInitialPriceEstimateResponse,
} from "../../models/Category/InitialPriceEstimate";
import { InitialPriceEstimateQueryKeys } from "./keys";

interface UseInitialPriceEstimateCategoriesOptions {
  searchTerm?: string;
  /** True for priced categories only, false for the rest, undefined for all. */
  configured?: boolean;
  enabled?: boolean;
}

/** Categories for the priced list on the left and for the Add picker. Filtered by the API. */
export function useInitialPriceEstimateCategories({
  searchTerm = "",
  configured,
  enabled = true,
}: UseInitialPriceEstimateCategoriesOptions = {}) {
  const { get } = useApiHelper();

  const params = new URLSearchParams({ searchTerm });
  if (configured !== undefined) params.set("configured", String(configured));

  const categories = useQuery({
    queryKey: InitialPriceEstimateQueryKeys.categories(searchTerm, configured),
    queryFn: () =>
      get<IDefaultResponse<IInitialPriceEstimateCategorySummary[]>>(
        `/initial-price-estimate/categories?${params.toString()}`,
      ).then((res) => res.data),
    enabled,
  });

  return { categories };
}

/**
 * The configuration for one category. A category not on the list yet comes back with defaults and
 * `isSaved: false`, so a direct link still renders.
 */
export function useInitialPriceEstimate(partCategoryId: string | undefined) {
  const { get } = useApiHelper();

  const config = useQuery({
    queryKey: InitialPriceEstimateQueryKeys.single(partCategoryId),
    queryFn: () =>
      get<IDefaultResponse<IInitialPriceEstimateResponse>>(
        `/initial-price-estimate/categories/${partCategoryId}`,
      ).then((res) => res.data),
    enabled: !!partCategoryId,
  });

  return { config };
}

/** The default AI match prompt. Fetched only while its modal is open, like the Serp settings. */
export function useInitialPriceEstimateAiPrompt(enabled: boolean) {
  const { get } = useApiHelper();

  const aiPrompt = useQuery({
    queryKey: InitialPriceEstimateQueryKeys.aiPrompt(),
    queryFn: () =>
      get<IDefaultResponse<IAiResultFilterPrompt>>(
        `/initial-price-estimate/ai-prompt`,
      ).then((res) => res.data),
    enabled,
  });

  return { aiPrompt };
}
